import { useEffect, useState } from 'react';
import { Typography, Card } from '@hid-galaxy-ui/galaxy-react';
import { useDispatch, useSelector } from 'react-redux';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';

import { RootStore } from '../../../store/reducers';
import {
  getAccountDetails,
  getDisabilitiesCategoriesApi,
} from '../../../services/app-user/app-user';
import { getTranslation, isExistInArray } from '../../../utils/functions';
import { userStateSelector } from '../../../store/selectors/userSelectors';
import PageLoader from '../../../components/PageLoader';
import userActions from '../../../store/actions/userActions';
import { getFormattedDate } from '../../../utils/dateUtils';
import { GENDER_ENUM_TRANSLATIONS } from '../constant';
import { getCountryNameByCode } from '../../../constants/countryList';
import './AccountDetails.scss';
import FilePreview from '../../../components/FilePreview/FilePreview';
import { IDisabilitiesCategoriesApiResponse } from '../../../services/app-user/IAppUser';
import { getDocumentLabelsAndTemplateUrls } from '../components/DisabilityCategories/DisabilityCategoriesHelper';

interface IAccountDetailsProps {
  fieldsToDisplay?: string[];
}

const AccountDetails = (props: IAccountDetailsProps) => {
  const { fieldsToDisplay } = props;

  const dispatch = useDispatch();
  const [disabilitiesCategories, setDisabilitiesCategories] = useState<
    IDisabilitiesCategoriesApiResponse[]
  >([]);

  const { accountData } = useSelector((state: RootStore) => ({
    accountData: userStateSelector(state),
  }));

  useEffect(() => {
    if (!accountData?.email) {
      fetchAccountDetails();
    }
    getDisabilitiesCategories();
  }, []);

  const fetchAccountDetails = async () => {
    try {
      const response = await getAccountDetails();
      dispatch(userActions.setAccountData(response));
    } catch (err) {}
  };

  const getDisabilitiesCategories = async () => {
    try {
      const response = await getDisabilitiesCategoriesApi();
      setDisabilitiesCategories(response);
    } catch (error) {
      console.log(error);
    }
  };

  const getSelectedDisabilityCategory = () => {
    if (accountData?.disabilityCategoryCode) {
      return disabilitiesCategories?.find(
        (category: IDisabilitiesCategoriesApiResponse) =>
          category.categoryCode === accountData.disabilityCategoryCode,
      );
    }
    return null;
  };

  const renderIdDocuments = () => {
    return (
      <div>
        <Typography
          variant={TypographyVariantEnum.H3}
        >{`${getTranslation('DOCUMENT_ID')}:`}</Typography>
        {accountData?.idDocuments?.map((url: string) => (
          <div className="hid-flex">
            <FilePreview previewMode file={url} useAppHubApi />
          </div>
        ))}
      </div>
    );
  };

  const renderDisabilityCategoryAndDocuments = () => {
    const selectedDisabilityCategory = getSelectedDisabilityCategory();
    const documentLabelsAndTemplateUrls = getDocumentLabelsAndTemplateUrls(
      selectedDisabilityCategory?.description,
    );
    return (
      <>
        {' '}
        {accountData?.disabilityCategoryCode && (
          <>
            <div className="hid-spacing__mb-04 details-container__item">
              <Typography
                variant={TypographyVariantEnum.H3}
              >{`${getTranslation('TYPE')}`}</Typography>
              <Typography variant={TypographyVariantEnum.BodyShortMarketing}>
                {selectedDisabilityCategory?.categoryName}
              </Typography>
            </div>
            {accountData?.documentUrls &&
              accountData?.documentUrls.length > 0 && (
                <div className="hid-spacing__mb-04">
                  <Typography
                    variant={TypographyVariantEnum.H3}
                  >{`${getTranslation('CATEGORY_DOCUMENTS')}:`}</Typography>
                  {accountData?.documentUrls?.map(
                    (url: string, index: number) => (
                      <>
                        <Typography
                          className="hid-spacing__mt-03"
                          variant={TypographyVariantEnum.BodyLongMarketing}
                        >
                          {documentLabelsAndTemplateUrls[index]?.label}
                        </Typography>

                        <div className="hid-flex">
                          <FilePreview previewMode file={url} useAppHubApi />
                        </div>
                      </>
                    ),
                  )}
                </div>
              )}
          </>
        )}
      </>
    );
  };

  const getAccountDetailsObject = () => {
    const detailsObjectToDisplay = {};
    const detailsObject = {
      firstName: {
        label: getTranslation('FIRSTNAME'),
        value: accountData?.firstName,
      },
      lastName: {
        label: getTranslation('LASTNAME'),
        value: accountData?.lastName,
      },
      email: { label: getTranslation('EMAIL'), value: accountData?.email },
      mobileNumber: {
        label: getTranslation('MOBILENUMBER'),
        value: accountData?.mobileNumber,
      },
      dateofbirth: {
        label: getTranslation('DATEOFBIRTH'),
        value: getFormattedDate(accountData?.dateOfBirth),
      },
      dateOfBirth: {
        label: getTranslation('COUNTRYOFBIRTH'),
        value: getCountryNameByCode(accountData?.countryOfBirth),
      },
      gender: {
        label: getTranslation('GENDER'),
        value: accountData?.gender
          ? getTranslation(GENDER_ENUM_TRANSLATIONS[accountData?.gender])
          : '',
      },
      placeOfBirth: {
        label: getTranslation('PLACEOFBIRTH'),
        value: accountData?.placeOfBirth,
      },
      fiscalCode: {
        label: getTranslation('FISCALCODE'),
        value: accountData?.fiscalCode,
      },
    };

    if (fieldsToDisplay && fieldsToDisplay.length > 0) {
      Object.keys(detailsObject)?.forEach((key: any) => {
        if (isExistInArray(fieldsToDisplay, key)) {
          detailsObjectToDisplay[key] = detailsObject[key];
        }
      });
    }

    return detailsObjectToDisplay;
  };

  const renderDetailsForm = () => {
    const detailsObject = getAccountDetailsObject();
    return (
      <Card>
        <>
          <div className="hid-layout__mt-05 details-container">
            {Object.entries(detailsObject).map(([key, obj]: any) => (
              <div className="details-container__item">
                <Typography variant={TypographyVariantEnum.H3}>
                  {obj.label}
                </Typography>
                <Typography variant={TypographyVariantEnum.BodyShortMarketing}>
                  {obj.value}
                </Typography>
              </div>
            ))}

            {isExistInArray(fieldsToDisplay, 'disabilityCategoryCode') &&
              renderDisabilityCategoryAndDocuments()}

            {isExistInArray(fieldsToDisplay, 'idDocuments') &&
              renderIdDocuments()}
          </div>
        </>
      </Card>
    );
  };

  return <>{accountData?.email ? renderDetailsForm() : <PageLoader />}</>;
};

export default AccountDetails;
